import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { useAIAction } from '../../hooks/useAIAction';
import { useGenerationStatus } from '../../hooks/useGenerationStatus';

interface VoiceLibraryBrowserProps {
  open: boolean;
  onClose: () => void;
  onSelectVoice: (voiceId: string, voiceName: string) => void;
}

// ---------------------------------------------------------------------------
// Voice catalogue — Fish Audio S2 Pro (primary) + Grok TTS (secondary)
// ---------------------------------------------------------------------------

interface Voice {
  id: string;
  name: string;
  gender: 'female' | 'male';
  style: string;
  provider: 'fish-audio' | 'grok';
}

const VOICES: Voice[] = [
  // Fish Audio S2 Pro — curated presets with real reference IDs
  {
    id: 'f8dfe9c83081432386f143e2fe9767ef',
    name: 'Book Record',
    gender: 'male',
    style: 'narrator, professional',
    provider: 'fish-audio',
  },
  {
    id: 'd8a1340984ee4b63ad1ffae27a6a4339',
    name: 'Lively Guy',
    gender: 'male',
    style: 'lively, energetic',
    provider: 'fish-audio',
  },
  {
    id: '7131193661114896bb7ca2ec3d0e3e03',
    name: 'Commander',
    gender: 'male',
    style: 'deep, authoritative',
    provider: 'fish-audio',
  },
  {
    id: 'bf322df2096a46f18c579d0baa36f41d',
    name: 'Gentle Baritone',
    gender: 'male',
    style: 'soft, calm',
    provider: 'fish-audio',
  },
  {
    id: '92a2600282e547f098b4a8de1bc9a44a',
    name: 'Young Professional',
    gender: 'male',
    style: 'authoritative, professional',
    provider: 'fish-audio',
  },
  {
    id: '692a2e659a8748558a1abc97ec051e0e',
    name: 'Everyman',
    gender: 'male',
    style: 'neutral, versatile',
    provider: 'fish-audio',
  },
  {
    id: '88872b3d83694d8490b55d75480205a0',
    name: 'Epic Announcer',
    gender: 'male',
    style: 'deep, dramatic',
    provider: 'fish-audio',
  },
  {
    id: '21d8216af9ad4a9a96149845ecd89850',
    name: 'Lavanna',
    gender: 'female',
    style: 'narrator, warm',
    provider: 'fish-audio',
  },
  {
    id: '51dbfa7f664147f6b589028449b0776a',
    name: 'Authority',
    gender: 'female',
    style: 'professional, confident',
    provider: 'fish-audio',
  },
  {
    id: 'c8f64deb39914cfca7f47ccfc3bca82f',
    name: 'Narrator',
    gender: 'female',
    style: 'clear diction',
    provider: 'fish-audio',
  },
  {
    id: 'ebc2e8d516e0460aa085106d29a1ccfc',
    name: 'Little Star',
    gender: 'female',
    style: 'youthful, bright',
    provider: 'fish-audio',
  },
  {
    id: 'bd8b049828ab44dc9b692f83a4e8b15d',
    name: 'Whisper',
    gender: 'female',
    style: 'soft, gentle',
    provider: 'fish-audio',
  },
  {
    id: '98655a12fa944e26b274c535e5e03842',
    name: 'Trickster',
    gender: 'female',
    style: 'mischievous',
    provider: 'fish-audio',
  },
  {
    id: 'cde85802e4d645bfa3add12ce38a726d',
    name: 'Storyteller',
    gender: 'female',
    style: 'narrator-like',
    provider: 'fish-audio',
  },
  {
    id: '5ac6fb7171ba419190700620738209d8',
    name: 'Mei',
    gender: 'female',
    style: 'medium, clear',
    provider: 'fish-audio',
  },
  {
    id: '6b4b604f01eb419c8a7ff773bb6812ff',
    name: 'Southern Belle',
    gender: 'female',
    style: 'southern, warm',
    provider: 'fish-audio',
  },
  // Grok TTS
  { id: 'sal', name: 'Sal', gender: 'male', style: 'smooth', provider: 'grok' },
  {
    id: 'eve',
    name: 'Eve',
    gender: 'female',
    style: 'energetic',
    provider: 'grok',
  },
  { id: 'ara', name: 'Ara', gender: 'female', style: 'warm', provider: 'grok' },
  {
    id: 'leo',
    name: 'Leo',
    gender: 'male',
    style: 'authoritative',
    provider: 'grok',
  },
  {
    id: 'rex',
    name: 'Rex',
    gender: 'male',
    style: 'confident',
    provider: 'grok',
  },
];

const PREVIEW_TEXT =
  'The ancient library was silent save for the whisper of turning pages. She traced her finger along the faded text, searching for the passage that would change everything.';

type GenderFilter = 'all' | 'female' | 'male';
type ProviderFilter = 'all' | 'fish-audio' | 'grok';

export function VoiceLibraryBrowser({
  open,
  onClose,
  onSelectVoice,
}: VoiceLibraryBrowserProps) {
  const [search, setSearch] = useState('');
  const [genderFilter, setGenderFilter] = useState<GenderFilter>('all');
  const [providerFilter, setProviderFilter] = useState<ProviderFilter>('all');
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');

  // Preview state
  const [previewJobId, setPreviewJobId] = useState<string | null>(null);
  const [previewingVoiceId, setPreviewingVoiceId] = useState<string | null>(
    null,
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const audioAction = useAIAction('audio-generation');
  const { event: genEvent } = useGenerationStatus(previewJobId);

  // Handle generation events from SignalR
  useEffect(() => {
    if (!genEvent || !isGenerating || !previewJobId) return;
    if (genEvent.jobId !== previewJobId) return;

    if (genEvent.status === 'complete') {
      setIsGenerating(false);
      const payload = genEvent.payload as { url?: string } | undefined;
      if (payload?.url) {
        const audio = new Audio(payload.url);
        audioRef.current = audio;
        audio.onended = () => {
          setIsPlaying(false);
          setPreviewingVoiceId(null);
        };
        audio.play();
        setIsPlaying(true);
      }
    }
    if (genEvent.status === 'failed') {
      setIsGenerating(false);
      setPreviewingVoiceId(null);
      setSnackMessage('Preview generation failed');
      setSnackOpen(true);
    }
  }, [genEvent, isGenerating, previewJobId]);

  // Stop audio when dialog closes
  useEffect(() => {
    if (!open) {
      audioRef.current?.pause();
      audioRef.current = null;
      setIsPlaying(false);
      setPreviewingVoiceId(null);
      setIsGenerating(false);
    }
  }, [open]);

  const filtered = useMemo(() => {
    return VOICES.filter((v) => {
      if (genderFilter !== 'all' && v.gender !== genderFilter) return false;
      if (providerFilter !== 'all' && v.provider !== providerFilter)
        return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        return (
          v.name.toLowerCase().includes(q) || v.style.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [search, genderFilter, providerFilter]);

  const handleSelect = (voice: Voice) => {
    onSelectVoice(voice.id, voice.name);
    onClose();
  };

  const handlePreview = async (voice: Voice) => {
    // Toggle off if already playing this voice
    if (isPlaying && previewingVoiceId === voice.id) {
      audioRef.current?.pause();
      audioRef.current = null;
      setIsPlaying(false);
      setPreviewingVoiceId(null);
      return;
    }

    // Stop any currently playing audio
    audioRef.current?.pause();
    audioRef.current = null;
    setIsPlaying(false);

    setPreviewJobId(null);
    setPreviewingVoiceId(voice.id);
    setIsGenerating(true);

    try {
      const result = await audioAction.mutateAsync({
        text: PREVIEW_TEXT,
        voice_id: voice.id,
        stability: 0.75,
        similarity_boost: 0.75,
      });
      setPreviewJobId(result.jobId);
    } catch {
      setIsGenerating(false);
      setPreviewingVoiceId(null);
      setSnackMessage('Failed to start preview generation');
      setSnackOpen(true);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Voice Library</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              placeholder="Search voices..."
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography variant="caption" color="text.secondary">
                Gender:
              </Typography>
              {(['all', 'female', 'male'] as const).map((g) => (
                <Chip
                  key={g}
                  label={
                    g === 'all' ? 'All' : g.charAt(0).toUpperCase() + g.slice(1)
                  }
                  variant={genderFilter === g ? 'filled' : 'outlined'}
                  color={genderFilter === g ? 'primary' : 'default'}
                  size="small"
                  onClick={() => setGenderFilter(g)}
                  clickable
                />
              ))}

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 2 }}
              >
                Provider:
              </Typography>
              {(
                [
                  ['all', 'All'],
                  ['fish-audio', 'Fish Audio'],
                  ['grok', 'Grok'],
                ] as const
              ).map(([value, label]) => (
                <Chip
                  key={value}
                  label={label}
                  variant={providerFilter === value ? 'filled' : 'outlined'}
                  color={providerFilter === value ? 'primary' : 'default'}
                  size="small"
                  onClick={() => setProviderFilter(value as ProviderFilter)}
                  clickable
                />
              ))}
            </Stack>

            {filtered.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 4, textAlign: 'center' }}
              >
                No voices match your filters.
              </Typography>
            )}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 2,
                pb: 1,
              }}
            >
              {filtered.map((voice) => {
                const isThisGenerating =
                  isGenerating && previewingVoiceId === voice.id;
                const isThisPlaying =
                  isPlaying && previewingVoiceId === voice.id;

                return (
                  <Card key={voice.id} variant="outlined">
                    <CardContent sx={{ pb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {voice.name}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{ mt: 1 }}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Chip
                          label={voice.gender}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={voice.style}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                        <Chip
                          label={
                            voice.provider === 'fish-audio'
                              ? 'Fish Audio'
                              : 'Grok'
                          }
                          size="small"
                          variant="outlined"
                          color={
                            voice.provider === 'fish-audio'
                              ? 'success'
                              : 'default'
                          }
                        />
                      </Stack>
                    </CardContent>
                    <CardActions>
                      <Tooltip
                        title={
                          isThisGenerating
                            ? 'Generating...'
                            : isThisPlaying
                              ? 'Stop'
                              : 'Preview voice'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handlePreview(voice)}
                            color="primary"
                            disabled={isGenerating && !isThisGenerating}
                          >
                            {isThisGenerating ? (
                              <CircularProgress size={20} />
                            ) : isThisPlaying ? (
                              <PauseCircleIcon />
                            ) : (
                              <PlayCircleIcon />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSelect(voice)}
                      >
                        Select
                      </Button>
                    </CardActions>
                  </Card>
                );
              })}
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        message={snackMessage}
      />
    </>
  );
}
